import type { FC } from "react";
import { useMemo } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";

import { Scrollbar } from "../../../components/scrollbar";
import { usePathname } from "../../../hooks/use-pathname";
import { paths } from "../../../paths";
import type { NavColor } from "../../../types/settings";

import { footerSection, useThemeConfig, type Section } from "../config";
import { MobileNavSection } from "./mobile-nav-section";
import { Logout } from "../../../svgs/logout";
import { ButtonBase, Divider, IconButton, SvgIcon } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useRouter } from "../../../hooks/use-router";
import { useRiskBannerHeight } from "../../../hooks/ace/use-risk-banner-height";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";

const MOBILE_NAV_WIDTH = 280;
const MOBILE_NAV_WIDTH_CLOSED = 0;

const useCssVars = (color: NavColor): Record<string, string> => {
  const theme = useTheme();
  const themeConfig = useThemeConfig(theme);

  return useMemo((): Record<string, string> => {
    return themeConfig?.[color]?.[theme.palette.mode] || {};
  }, [theme, color]);
};

interface MobileNavProps {
  color?: NavColor;
  onClose?: () => void;
  onOpen?: () => void;
  open?: boolean;
  sections?: Section[];
}

const LogoutButton = ({ onClick }: { onClick: () => void }) => (
  <Stack
    spacing={2}
    sx={{
      flexGrow: 1,
      px: 2,
    }}
  >
    <ButtonBase
      data-cy="logout-button"
      onClick={onClick}
      sx={{
        alignItems: "center",
        borderRadius: 1,
        display: "flex",
        justifyContent: "flex-start",
        px: "16px",
        py: "16px",
        textAlign: "left",
        width: "100%",
        "&:hover": {
          backgroundColor: "var(--nav-item-hover-bg)",
        },
      }}
    >
      <Box
        component="span"
        sx={{
          alignItems: "center",
          color: "var(--nav-item-icon-color)",
          display: "inline-flex",
          justifyContent: "center",
          mr: 2,
        }}
      >
        <Logout />
      </Box>
      <Box
        component="span"
        sx={{
          color: "error.light",
          flexGrow: 1,
          fontFamily: (theme) => theme.typography.fontFamily,
          fontSize: 14,
          fontWeight: 600,
          lineHeight: "24px",
          whiteSpace: "nowrap",
        }}
      >
        Log out
      </Box>
    </ButtonBase>
  </Stack>
);

export const MobileNav: FC<MobileNavProps> = (props) => {
  const { color = "evident", open, onClose, onOpen, sections = [] } = props;
  const router = useRouter();
  const pathname = usePathname();
  const riskBannerHeight = useRiskBannerHeight();

  const cssVars = useCssVars(color);
  const theme = useTheme();
  const sideNavWidth = (open && MOBILE_NAV_WIDTH) || MOBILE_NAV_WIDTH_CLOSED;

  return (
    <>
      <Stack
        sx={{
          display: "flex",
          flexDirection: "row",
          p: 1,
          zIndex: 10,
          position: "sticky",
          top: 0,
          backgroundColor: theme.palette.background.paper,
          boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.24)",
          paddingTop: 10 + riskBannerHeight + "px",
        }}
      >
        <IconButton onClick={onOpen}>
          <SvgIcon>
            <MenuIcon />
          </SvgIcon>
        </IconButton>
      </Stack>
      <Drawer
        anchor="left"
        onClose={onClose}
        open={open}
        PaperProps={{
          sx: {
            ...cssVars,
            backgroundColor: "var(--nav-bg)",
            borderRightColor: "var(--nav-border-color)",
            borderRightWidth: "1px",
            borderRightStyle: "solid",
            paddingTop: `${riskBannerHeight}px`,
            color: "var(--nav-color)",
            width: MOBILE_NAV_WIDTH,
          },
        }}
        variant="temporary"
      >
        {open && (
          <IconButton
            data-cy="sidebar-close-button"
            onClick={onClose}
            size="small"
            sx={{
              ...cssVars,
              position: "fixed",
              top: `${riskBannerHeight + 80}px`,
              left: sideNavWidth - 12 + "px",
              height: "24px",
              width: "24px",
              color: "var(--nav-item-chevron-color)",
              backgroundColor: "var(--nav-bg)",
              "&:hover": {
                backgroundColor: "var(--nav-collapse-hover)",
              },
              border: "1px var(--nav-border-color) solid",
              zIndex: (theme) => theme.zIndex.drawer + 1,
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: "19px", pr: "1px" }} />
          </IconButton>
        )}
        <Scrollbar
          sx={{
            height: "100%",
            "& .simplebar-content": {
              height: "100%",
            },
            "& .simplebar-scrollbar:before": {
              background: "var(--nav-scrollbar-color)",
            },
          }}
        >
          <Stack sx={{ height: "100%" }}>
            <Box component="div" sx={{ pt: 3, pb: 2, px: 2 }}>
              <img
                data-cy="mobile-nav-archax-logo"
                src="/assets/archax-web-logo.svg"
                alt="archax-logo"
              />
            </Box>
            <Stack
              component="nav"
              spacing={2}
              sx={{
                flexGrow: 1,
                px: 2,
              }}
            >
              {sections.map((section, index) => (
                <MobileNavSection
                  items={section.items}
                  key={index}
                  pathname={pathname}
                  subheader={section.subheader}
                />
              ))}
            </Stack>
            <Box>
              <Stack
                spacing={2}
                sx={{
                  flexGrow: 1,
                  p: 2,
                }}
              >
                {footerSection.map((section, index) => (
                  <MobileNavSection
                    items={section.items}
                    key={index}
                    pathname={pathname}
                    subheader={section.subheader}
                  />
                ))}
              </Stack>
              <Divider />
              <LogoutButton
                data-cy="logout-button"
                onClick={() => {
                  router.push(paths.logout);
                }}
              />
            </Box>
          </Stack>
        </Scrollbar>
      </Drawer>
    </>
  );
};

MobileNav.propTypes = {
  color: PropTypes.oneOf<NavColor>(["blend-in", "discrete", "evident"]),
  onClose: PropTypes.func,
  open: PropTypes.bool,
  sections: PropTypes.array,
};
